import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

import { CustomFetchError } from "@/client/custom-fetch";
import { toast } from "@/components/ui/use-toast";
import { useI18n } from "@/context/i18n";

import {
  getCampaignContactsTemplate,
  uploadCampaignContacts,
} from "./campaign-upload.api";
import { IUseCampanhaUpload } from "./campaign-upload.hooks.interface";
import {
  campaignUploadFormSchema,
  TCampaignUploadSchemaData,
} from "./campaign-upload.schema";
import {
  TCampaignUploadParsedRow,
  TCampaignUploadPreviewRow,
  TCampaignUploadRowStatus,
  TCampaignUploadSummary,
} from "./campaign-upload.types";

const PREVIEW_LIMIT = 30;

export const useCampanhaUpload = (
  campaignId?: string,
  clientId?: string
): IUseCampanhaUpload => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [parsedRows, setParsedRows] = React.useState<TCampaignUploadParsedRow[]>([]);
  const [previewRows, setPreviewRows] = React.useState<TCampaignUploadPreviewRow[]>([]);
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [uploadSummary, setUploadSummary] =
    React.useState<TCampaignUploadSummary | null>(null);
  const [isParsingFile, setIsParsingFile] = React.useState<boolean>(false);

  const form = useForm<TCampaignUploadSchemaData>({
    resolver: zodResolver(campaignUploadFormSchema),
    defaultValues: {
      file: null,
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!campaignId) {
        throw new Error(t("campaigns.upload.errors.campaignIdMissing"));
      }
      return uploadCampaignContacts(campaignId, file);
    },
    onSuccess: (response) => {
      setUploadSummary(response);
      setPreviewRows(buildPreviewRows(parsedRows, response.errors));
      toast({
        title: t("generic.success"),
        description: t("campaigns.upload.success.processed"),
      });

      if (clientId && campaignId) {
        setTimeout(() => {
          navigate(`/clients/${clientId}/campaigns/${campaignId}/details`);
        }, 1500);
      }
    },
    onError: (error: unknown) => {
      let message = t("campaigns.upload.errors.generic");

      if (error instanceof CustomFetchError) {
        if (error.status === 404) {
          message = t("campaigns.upload.errors.campaignNotFound");
        } else if (error.status === 400) {
          message = error.message;
        }
      } else if (error instanceof Error && error.message) {
        message = error.message;
      }

      toast({
        title: t("generic.error"),
        description: message,
        variant: "destructive",
      });
    },
  });

  const downloadTemplateMutation = useMutation({
    mutationFn: getCampaignContactsTemplate,
    onSuccess: (template) => {
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet([
        template.headers,
        template.sample_row,
      ]);

      XLSX.utils.book_append_sheet(workbook, worksheet, template.sheet_name);
      XLSX.writeFile(workbook, "leads-template.xlsx");
    },
    onError: () => {
      toast({
        title: t("generic.error"),
        description: t("campaigns.upload.errors.templateDownload"),
        variant: "destructive",
      });
    },
  });

  const parseFile = React.useCallback(
    async (file: File): Promise<TCampaignUploadParsedRow[]> => {
      const extension = file.name.split(".").pop()?.toLowerCase();
      if (extension === "csv") {
        return parseCsvFile(file);
      }

      if (extension === "xlsx") {
        return parseXlsxFile(file);
      }

      throw new Error(t("campaigns.upload.validation.invalidExtension"));
    },
    [t]
  );

  const handleFileSelect = React.useCallback(
    async (file: File | null) => {
      setUploadSummary(null);
      setPreviewRows([]);
      setParsedRows([]);
      setSelectedFile(file);
      form.setValue("file", file, { shouldValidate: true });

      if (!file) {
        return;
      }

      setIsParsingFile(true);
      try {
        const rows = await parseFile(file);
        setParsedRows(rows);
        setPreviewRows(
          rows.slice(0, PREVIEW_LIMIT).map((row) => ({
            ...row,
            status: "valid",
          }))
        );
      } catch (error) {
        const rawMessage =
          error instanceof Error
            ? error.message
            : "campaigns.upload.errors.invalidFile";
        const errorMessage = rawMessage.startsWith("campaigns.")
          ? t(rawMessage)
          : rawMessage;

        toast({
          title: t("generic.error"),
          description: errorMessage,
          variant: "destructive",
        });
      } finally {
        setIsParsingFile(false);
      }
    },
    [form, parseFile, t]
  );

  const handleSubmit = React.useCallback(
    (values: TCampaignUploadSchemaData) => {
      if (!values.file) {
        return;
      }

      uploadMutation.mutate(values.file);
    },
    [uploadMutation]
  );

  return {
    form,
    selectedFile,
    previewRows,
    uploadSummary,
    isUploading: uploadMutation.isPending,
    isParsingFile,
    isDownloadingTemplate: downloadTemplateMutation.isPending,
    downloadTemplate: () => downloadTemplateMutation.mutate(),
    handleFileSelect,
    handleSubmit,
  };
};

const buildPreviewRows = (
  parsedRows: TCampaignUploadParsedRow[],
  errors: Array<{ line: number; reason: string }>
): TCampaignUploadPreviewRow[] => {
  const statusByLine = new Map<number, { status: TCampaignUploadRowStatus; reason: string }>();

  errors.forEach((error) => {
    statusByLine.set(error.line, {
      status: classifyStatus(error.reason),
      reason: error.reason,
    });
  });

  return parsedRows.slice(0, PREVIEW_LIMIT).map((row) => {
    const matchedError = statusByLine.get(row.line);
    if (!matchedError) {
      return {
        ...row,
        status: "valid",
      };
    }

    return {
      ...row,
      status: matchedError.status,
      reason: matchedError.reason,
    };
  });
};

const classifyStatus = (reason: string): TCampaignUploadRowStatus => {
  const normalized = reason.toLowerCase();
  if (normalized.includes("duplic")) {
    return "duplicate";
  }

  return "invalid";
};

const parseCsvFile = async (file: File): Promise<TCampaignUploadParsedRow[]> => {
  const content = (await file.text()).replace(/^\uFEFF/, "");
  const lines = content
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (lines.length <= 1) {
    return [];
  }

  const delimiter = detectDelimiter(lines[0]);
  const headers = splitCsvLine(lines[0], delimiter).map((header) => header.trim());
  const columnsMap = resolveImportColumns(headers);

  if (columnsMap.primaryPhoneIndex < 0 && columnsMap.secondaryPhoneIndex < 0) {
    throw new Error("campaigns.upload.errors.phoneColumnMissing");
  }

  return lines.slice(1).map((line, index) => {
    const columns = splitCsvLine(line, delimiter);
    const primaryPhone =
      columnsMap.primaryPhoneIndex >= 0
        ? (columns[columnsMap.primaryPhoneIndex] ?? "").trim()
        : "";
    const secondaryPhone =
      columnsMap.secondaryPhoneIndex >= 0
        ? (columns[columnsMap.secondaryPhoneIndex] ?? "").trim()
        : "";
    const phone = primaryPhone || secondaryPhone;

    const firstName =
      columnsMap.firstNameIndex >= 0
        ? (columns[columnsMap.firstNameIndex] ?? "").trim()
        : "";
    const lastName =
      columnsMap.lastNameIndex >= 0
        ? (columns[columnsMap.lastNameIndex] ?? "").trim()
        : "";
    const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
    const fallbackName =
      columnsMap.fallbackNameIndex >= 0
        ? (columns[columnsMap.fallbackNameIndex] ?? "").trim()
        : "";

    const company =
      columnsMap.companyIndex >= 0
        ? (columns[columnsMap.companyIndex] ?? "").trim()
        : "";
    const email =
      columnsMap.emailIndex >= 0
        ? (columns[columnsMap.emailIndex] ?? "").trim()
        : "";
    const documentNumber =
      columnsMap.documentNumberIndex >= 0
        ? (columns[columnsMap.documentNumberIndex] ?? "").trim()
        : "";

    return {
      line: index + 2,
      name: fullName || fallbackName,
      company: company || undefined,
      first_name: firstName || undefined,
      last_name: lastName || undefined,
      email: email || undefined,
      phone,
      phone_2: secondaryPhone !== phone ? secondaryPhone : undefined,
      document_number: documentNumber || undefined,
    };
  });
};

const parseXlsxFile = async (file: File): Promise<TCampaignUploadParsedRow[]> => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });
  const firstSheetName = workbook.SheetNames[0];

  if (!firstSheetName) {
    return [];
  }

  const worksheet = workbook.Sheets[firstSheetName];
  const rows = XLSX.utils.sheet_to_json<Array<string | number>>(worksheet, {
    header: 1,
    raw: false,
    defval: "",
  });

  if (rows.length <= 1) {
    return [];
  }

  const headers = rows[0].map((cell) => String(cell).trim());
  const columnsMap = resolveImportColumns(headers);

  if (columnsMap.primaryPhoneIndex < 0 && columnsMap.secondaryPhoneIndex < 0) {
    throw new Error("campaigns.upload.errors.phoneColumnMissing");
  }

  return rows.slice(1).map((row, index) => {
    const firstName =
      columnsMap.firstNameIndex >= 0
        ? String(row[columnsMap.firstNameIndex] ?? "").trim()
        : "";
    const lastName =
      columnsMap.lastNameIndex >= 0
        ? String(row[columnsMap.lastNameIndex] ?? "").trim()
        : "";
    const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
    const fallbackName =
      columnsMap.fallbackNameIndex >= 0
        ? String(row[columnsMap.fallbackNameIndex] ?? "").trim()
        : "";
    const primaryPhone =
      columnsMap.primaryPhoneIndex >= 0
        ? String(row[columnsMap.primaryPhoneIndex] ?? "").trim()
        : "";
    const secondaryPhone =
      columnsMap.secondaryPhoneIndex >= 0
        ? String(row[columnsMap.secondaryPhoneIndex] ?? "").trim()
        : "";

    const company =
      columnsMap.companyIndex >= 0
        ? String(row[columnsMap.companyIndex] ?? "").trim()
        : "";
    const email =
      columnsMap.emailIndex >= 0
        ? String(row[columnsMap.emailIndex] ?? "").trim()
        : "";
    const documentNumber =
      columnsMap.documentNumberIndex >= 0
        ? String(row[columnsMap.documentNumberIndex] ?? "").trim()
        : "";

    return {
      line: index + 2,
      name: fullName || fallbackName,
      company: company || undefined,
      first_name: firstName || undefined,
      last_name: lastName || undefined,
      email: email || undefined,
      phone: primaryPhone || secondaryPhone,
      phone_2: secondaryPhone !== (primaryPhone || secondaryPhone) ? secondaryPhone : undefined,
      document_number: documentNumber || undefined,
    };
  });
};

const detectDelimiter = (headerLine: string): string => {
  const delimiters = [",", ";", "\t", "|"];
  return delimiters.reduce((selected, delimiter) => {
    const selectedCount = headerLine.split(selected).length;
    const currentCount = headerLine.split(delimiter).length;
    return currentCount > selectedCount ? delimiter : selected;
  }, ",");
};

const splitCsvLine = (line: string, delimiter: string): string[] => {
  const values: string[] = [];
  let current = "";
  let insideQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (insideQuotes && nextChar === '"') {
        current += '"';
        i += 1;
      } else {
        insideQuotes = !insideQuotes;
      }
      continue;
    }

    if (char === delimiter && !insideQuotes) {
      values.push(current);
      current = "";
      continue;
    }

    current += char;
  }

  values.push(current);
  return values;
};

const resolveImportColumns = (headers: string[]) => {
  return {
    primaryPhoneIndex: findHeaderIndex(headers, [
      "telefone",
      "phone",
      "celular",
      "whatsapp",
    ]),
    secondaryPhoneIndex: findHeaderIndex(headers, [
      "telefone 2",
      "telefone2",
      "phone 2",
      "phone2",
    ]),
    firstNameIndex: findHeaderIndex(headers, ["nome", "first name"]),
    lastNameIndex: findHeaderIndex(headers, ["sobrenome", "last name"]),
    fallbackNameIndex: findHeaderIndex(headers, ["name", "empresa"]),
    companyIndex: findHeaderIndex(headers, ["empresa", "company"]),
    emailIndex: findHeaderIndex(headers, ["email", "e-mail"]),
    documentNumberIndex: findHeaderIndex(headers, ["cnpj", "document", "cpf", "documento"]),
  };
};

const findHeaderIndex = (headers: string[], aliases: string[]): number => {
  const normalizedAliases = aliases.map((alias) => normalizeHeader(alias));
  return headers.findIndex((header) =>
    normalizedAliases.includes(normalizeHeader(header))
  );
};

const normalizeHeader = (value: string): string => {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
};
